import moment from 'moment'
import React, { useCallback, useContext, useRef } from 'react'

import BaseBookmarkToItineraryButton from '../../Common/BaseBookmarkToItineraryButton'

import { track } from 'analytics/analytics'
import { addExploreItemToItineraryEvent } from 'analytics/snowplow/events'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import noop from 'lib/function/noop'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import { clearRecentlySavedTripId } from 'storage/recentSavedTrip'
import { AddToItineraryDatesRequiredModal } from 'tripPlanner/components/Itinerary/AddToItineraryDatesRequiredModal'
import DatesDropdown from 'tripPlanner/components/Itinerary/DatesDropdown'
import DatesModal from 'tripPlanner/components/Itinerary/DatesModal'
import {
  itemIsVisibleInItinerary,
  customItemToUpdatePayload,
} from 'tripPlanner/components/Itinerary/utils'
import {
  HOTEL_TYPE,
  determineItemType,
} from 'tripPlanner/components/TripMaps/MapSearch'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesMC'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import {
  useCreateTripItem,
  useTrip,
  useUpdateTripItem,
} from 'tripPlanner/hooks/api'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { setCurrentSectionId } from 'tripPlanner/reducers/actions'
import {
  selectTripPlannerTemplateId,
  selectTripPlannerTemplateItemId,
} from 'tripPlanner/selectors'
import { ExploreItem } from 'tripPlanner/types/common'
import { getMatchingSavedExploreItem } from 'tripPlanner/utils/explore'
import { createExploreItemBookmarkPayload } from 'tripPlanner/utils/itemConversions/bookmarks/exploreItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

export interface Props {
  exploreItem: ExploreItem
  tripId?: string
  variant: 'chip' | 'button'
  onDatesRequiredMessage?: () => void
  onSave: () => void
}

function SearchBookmarkToItineraryButton({
  exploreItem,
  tripId: tripIdProp,
  variant,
  onDatesRequiredMessage = noop,
  onSave,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const showModal = useContext(ModalContext)
  const isMobile = useIsMobileScreen()
  const { canEdit } = useTripPermissions(tripId)

  const templateId = useAppSelector(selectTripPlannerTemplateId)
  const templateItemId = useAppSelector(selectTripPlannerTemplateItemId)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const {
    value: dropdownOpen,
    toggle: toggleDropdown,
    off: closeDropdown,
  } = useToggle()

  const savedItem = trip
    ? getMatchingSavedExploreItem(trip, exploreItem)
    : undefined

  const isVisibleInItinerary =
    !!savedItem && itemIsVisibleInItinerary(savedItem, trip)

  const { showSaveErrorSnackbar } = useBookmarkSnackbarHandlers()

  const dispatch = useAppDispatch()

  const { mutateAsync: createItem, isLoading: isCreating } = useCreateTripItem({
    onSuccess: (item) => {
      onSave()
      dispatch(setCurrentSectionId(itemSectionId(item.id)))
    },
    onError: (e) => {
      console.error(e)
      showSaveErrorSnackbar(trip!.name)

      // Clear the recently saved trip ID in case it errored because the trip was deleted
      clearRecentlySavedTripId()
    },
  })

  const { mutate: updateItem, isLoading: isUpdating } = useUpdateTripItem()

  const createTripItemIntoItinerary = useCallback(
    async (date: moment.Moment) => {
      if (!trip) {
        return
      }
      try {
        const type =
          determineItemType(exploreItem.types) === HOTEL_TYPE
            ? 'HOTEL'
            : 'ATTRACTION'

        const payload = createExploreItemBookmarkPayload({
          exploreItem,
          templateId,
          templateItemId,
          isInItinerary: true,
          startDate: date.format(ISO_DATE_FORMAT),
          type,
        })
        await createItem({
          tripId: trip.id,
          tripItem: payload,
        })
        showSnackbar('Added to itinerary', 'success')
      } catch (e) {
        // Do nothing - failure case is handled by the hook's onError handler
      }
    },
    [trip, exploreItem, templateId, templateItemId, createItem],
  )

  const addItemToItineraryWithDate = useCallback(
    async (date: moment.Moment) => {
      if (!trip || !savedItem) {
        return
      }
      const payload = {
        ...customItemToUpdatePayload(savedItem),
        isInItinerary: true,
        startDate: date.format(ISO_DATE_FORMAT),
      }
      updateItem(
        {
          tripId: trip.id,
          tripItemId: savedItem.id,
          tripItem: payload,
        },
        {
          onSuccess: () => {
            showSnackbar('Added to itinerary', 'success')
          },
        },
      )
    },
    [updateItem, trip, savedItem],
  )

  const removeFromItinerary = useCallback(async () => {
    closeDropdown()
    if (!trip || !savedItem) {
      return
    }
    const payload = {
      ...customItemToUpdatePayload(savedItem),
      isInItinerary: false,
      startDate: undefined,
    }
    updateItem(
      {
        tripId: trip.id,
        tripItemId: savedItem.id,
        tripItem: payload,
      },
      {
        onSuccess: () => {
          showSnackbar('Removed from itinerary', 'success')
        },
      },
    )
  }, [closeDropdown, trip, savedItem, updateItem])

  const onSelectDate = useCallback(
    (date: moment.Moment) => {
      closeDropdown()
      if (savedItem) {
        addItemToItineraryWithDate(date)
      } else {
        createTripItemIntoItinerary(date)
      }
      track(
        'click-explore-add-to-itinerary-trip-planner',
        addExploreItemToItineraryEvent(),
      )
    },
    [
      addItemToItineraryWithDate,
      createTripItemIntoItinerary,
      savedItem,
      closeDropdown,
    ],
  )

  const openDateSelector = useCallback(
    async (tripStartDate: moment.Moment, tripEndDate: moment.Moment) => {
      if (isMobile) {
        const selection = await showModal<moment.Moment | null | undefined>(
          <DatesModal
            selectedDate={
              isVisibleInItinerary ? savedItem.startDate : undefined
            }
            startDate={tripStartDate}
            endDate={tripEndDate}
          />,
        )
        if (selection === null) {
          removeFromItinerary()
        } else if (moment.isMoment(selection)) {
          onSelectDate(selection)
        }
      } else {
        toggleDropdown()
      }
    },
    [
      isMobile,
      isVisibleInItinerary,
      onSelectDate,
      removeFromItinerary,
      savedItem?.startDate,
      showModal,
      toggleDropdown,
    ],
  )

  const onRequestAddTripDates = useCallback(async () => {
    const result = await showModal<{
      startDate: moment.Moment | undefined
      endDate: moment.Moment | undefined
    }>(<EditTripDates tripId={tripId} />)
    if (result?.startDate && result.endDate) {
      openDateSelector(result.startDate, result.endDate)
    }
  }, [openDateSelector, showModal, tripId])

  const onButtonClick = useCallback(async () => {
    if (!trip) {
      return
    } else if (!trip.startDate || !trip.endDate) {
      onDatesRequiredMessage()
      const didRequestAddTripDates = await showModal<boolean>(
        <AddToItineraryDatesRequiredModal variation="exploreItem" />,
      )
      if (didRequestAddTripDates) {
        onRequestAddTripDates()
      }
    } else {
      openDateSelector(trip.startDate, trip.endDate)
    }
  }, [
    trip,
    onDatesRequiredMessage,
    showModal,
    onRequestAddTripDates,
    openDateSelector,
  ])

  if (!canEdit || !tripId) {
    return null
  }

  const isProcessing = isCreating || isUpdating

  return (
    <>
      <BaseBookmarkToItineraryButton
        itineraryDate={isVisibleInItinerary ? savedItem.startDate : undefined}
        onClick={onButtonClick}
        isProcessing={isProcessing}
        variant={variant}
        ref={triggerRef}
        withDropdownArrow
      />
      {dropdownOpen && trip?.startDate && trip.endDate && (
        <DatesDropdown
          isOpen
          onClose={closeDropdown}
          onSelectDate={onSelectDate}
          onSelectRemoveDate={removeFromItinerary}
          selectedDate={isVisibleInItinerary ? savedItem.startDate : undefined}
          minDate={trip.startDate}
          maxDate={trip.endDate}
          triggerRef={triggerRef}
        />
      )}
    </>
  )
}

export default SearchBookmarkToItineraryButton
